/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * OrganizerEventsPage
 * -------------------
 * Everything this organizer has created, newest first, and the way in to
 * creating another.
 *
 * The three empty-ish states are deliberately distinct, because they call for
 * three different actions:
 *   • loading          — say so, offer nothing
 *   • no organization  — the account is not set up; creating is hidden, because
 *                        offering a button that cannot work is worse than not
 *                        offering one
 *   • load failed      — a retry, NOT "no events yet"; telling someone their
 *                        events are gone when the request merely failed is the
 *                        single most alarming thing this screen could do
 *   • genuinely empty  — an invitation
 *
 * A fifth state was added with the filters: a list that is empty only because
 * of the current tab or search term. It must never borrow the "No events yet"
 * copy — the events exist, they are simply hidden by a control the organizer
 * can undo.
 *
 * Filtering is client-side on purpose. Every event is already in memory (the
 * endpoint returns the organization's full list), so a tab or a search term is
 * a slice of what we hold, not a new request. Nothing here can fail.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import useOrganizerOrg from '../../hooks/useOrganizerOrg';
import useOrganizerEvents from '../../hooks/useOrganizerEvents';
import EventStatusPill from '../../components/organizer/events/EventStatusPill';
import { EventCopyLinkButton } from '../../components/organizer/events/EventShareLink';
import { isLockedStatus } from '../../components/athlohive/eventStatus';
import { formatDateTime } from '../../components/athlohive/formatDateTime';
import { parseStoredUtc, utcToWallClock } from '../../utils/zonedDateTime';
import '../../styles/organizer.css';

/**
 * The tabs, and which statuses each one holds.
 *
 * Grouped by what the organizer would do about them rather than by the raw
 * status: "Live" is anything the public can currently see, which is two
 * statuses; "Finished" folds CANCELLED in with COMPLETED because both are over
 * and neither can be edited.
 */
const TABS = [
  { id: 'all', label: 'All', statuses: null },
  { id: 'live', label: 'Live', statuses: ['PUBLISHED', 'REGISTRATION_OPEN'] },
  { id: 'drafts', label: 'Drafts', statuses: ['DRAFT'] },
  { id: 'closed', label: 'Closed', statuses: ['REGISTRATION_CLOSED'] },
  { id: 'finished', label: 'Finished', statuses: ['COMPLETED', 'CANCELLED'] },
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * The day/month tile at the head of each row.
 *
 * Returns null for an undated event rather than inventing a date — a draft with
 * no date set is a real and common state, and showing today's date there would
 * be a lie the organizer might act on.
 */
/* The tile sits beside the row's own start time, which is formatted with
   formatDateTime(start, event.timezone). Read through new Date() the stored
   UTC would be taken as the VIEWER's wall clock, so the tile could show a
   different day from the line next to it — the two must agree. */
function tileParts(startDatetime, timeZone) {
  if (!startDatetime) return null;
  const wall = utcToWallClock(startDatetime, timeZone);
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(wall);
  if (!match) return null;
  const month = Number(match[2]) - 1;
  if (month < 0 || month > 11) return null;
  return { day: match[3], month: MONTHS[month] };
}

/** Milliseconds for sorting; undated events sort last, never first. */
function sortKey(startDatetime) {
  if (!startDatetime) return Number.NEGATIVE_INFINITY;
  // Stored naive UTC — parseStoredUtc reads it as that rather than as local.
  const parsed = parseStoredUtc(startDatetime);
  return parsed ? parsed.getTime() : Number.NEGATIVE_INFINITY;
}

/** The status word that drives the row's left edge and its date tile. */
function toneFor(status) {
  const value = String(status || '').toUpperCase();
  if (value === 'REGISTRATION_OPEN') return 'open';
  if (value === 'PUBLISHED') return 'pub';
  if (value === 'DRAFT') return 'draft';
  return 'over';
}

export default function OrganizerEventsPage() {
  const navigate = useNavigate();
  const organization = useOrganizerOrg();
  const { events, isLoading, isError, refetch } = useOrganizerEvents(organization.organizationId);

  const [tab, setTab] = React.useState('all');
  const [query, setQuery] = React.useState('');

  const canCreate = !organization.isMissing && !organization.isError;

  // Newest first. Sorting a copy — the hook's array is shared state, and
  // sorting in place would reorder it for anything else holding the reference.
  const ordered = React.useMemo(
    () => [...events].sort((a, b) => sortKey(b.start_datetime) - sortKey(a.start_datetime)),
    [events],
  );

  const counts = React.useMemo(() => {
    const out = { all: ordered.length };
    TABS.forEach((entry) => {
      if (!entry.statuses) return;
      out[entry.id] = ordered.filter(
        (event) => entry.statuses.includes(String(event.status || '').toUpperCase()),
      ).length;
    });
    return out;
  }, [ordered]);

  const visible = React.useMemo(() => {
    const active = TABS.find((entry) => entry.id === tab);
    const term = query.trim().toLowerCase();
    return ordered.filter((event) => {
      const inTab = !active || !active.statuses
        || active.statuses.includes(String(event.status || '').toUpperCase());
      const inSearch = !term || String(event.title || '').toLowerCase().includes(term);
      return inTab && inSearch;
    });
  }, [ordered, tab, query]);

  const isFiltered = tab !== 'all' || query.trim() !== '';

  return (
    <>
      <header className="org-topbar orge-bar">
        <div>
          <h1>Events</h1>
          <p className="org-sub">
            {organization.organizationName
              ? `Created by ${organization.organizationName}`
              : 'Create, publish and run events'}
          </p>
        </div>
        {canCreate && (
          <button
            type="button"
            className="org-btn"
            style={{ marginLeft: 'auto' }}
            onClick={() => navigate('/organizer/events/create')}
          >
            + New event
          </button>
        )}
      </header>

      <div className="org-content orge-surface">
        {organization.isMissing && (
          <div className="org-empty">
            <h2>Your account is not linked to an organization yet</h2>
            <p>
              Events are created by an organization, and yours has not been set up. Ask a platform
              administrator to create it and link your account — you will be able to create events
              as soon as they have.
            </p>
          </div>
        )}

        {organization.isError && (
          <div className="orgf-notice">
            <span>Your organization could not be loaded, so your events cannot be shown.</span>
            <button
              type="button"
              className="org-btn ghost"
              style={{ marginLeft: 'auto' }}
              onClick={() => organization.refetch()}
            >
              Retry
            </button>
          </div>
        )}

        {canCreate && isError && (
          <div className="orgf-notice">
            <span>
              Your events could not be loaded. They have not been lost — this screen simply could
              not reach the server.
            </span>
            <button
              type="button"
              className="org-btn ghost"
              style={{ marginLeft: 'auto' }}
              onClick={() => refetch()}
            >
              Retry
            </button>
          </div>
        )}

        {canCreate && (isLoading || organization.isLoading) && (
          <ul className="orgf-rows" role="status" aria-busy="true" aria-live="polite">
            <li className="sr-only-org">Loading your events…</li>
            {[0, 1, 2].map((i) => (
              <li className="orgf-row" key={i} aria-hidden="true">
                <div className="orgf-rowmain">
                  <div className="orgf-skelline" style={{ width: '42%', marginBottom: 8 }} />
                  <div className="orgf-skelline" style={{ width: '26%', height: 8, marginBottom: 0 }} />
                </div>
                <div className="orgf-skelline" style={{ width: 78, height: 18, borderRadius: 999 }} />
              </li>
            ))}
          </ul>
        )}

        {canCreate && !isLoading && !isError && events.length === 0 && (
          <div className="org-empty">
            <h2>No events yet</h2>
            <p>
              Your first event takes three steps: what it is, what people can enter, and how they
              register. You can save it as a draft at any point and come back.
            </p>
            <p style={{ marginTop: 15 }}>
              <button
                type="button"
                className="org-btn"
                onClick={() => navigate('/organizer/events/create')}
              >
                Create your first event
              </button>
            </p>
          </div>
        )}

        {/*
          The toolbar only appears once there is a list worth narrowing. On an
          empty account it would be five tabs reading zero and a search box over
          nothing — chrome that makes the screen look broken rather than new.
        */}
        {events.length > 0 && (
          <div className="orge-tools">
            <div className="orge-tabs" role="group" aria-label="Filter events by status">
              {TABS.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className={`orge-tab${tab === entry.id ? ' active' : ''}`}
                  aria-pressed={tab === entry.id}
                  onClick={() => setTab(entry.id)}
                >
                  {entry.label}
                  <i className="orge-tabn">{counts[entry.id] || 0}</i>
                </button>
              ))}
            </div>
            <div className="orge-search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-3.5-3.5" />
              </svg>
              <input
                id="organizer-events-search"
                type="search"
                className="orge-searchinput"
                placeholder="Search events"
                aria-label="Search events"
                value={query}
                onChange={(changeEvent) => setQuery(changeEvent.target.value)}
              />
            </div>
            <span className="orge-sort">Newest first</span>
          </div>
        )}

        {events.length > 0 && visible.length === 0 && isFiltered && (
          <div className="orge-nomatch">
            <p>No event matches this filter.</p>
            <button
              type="button"
              className="org-btn ghost"
              onClick={() => { setTab('all'); setQuery(''); }}
            >
              Show all events
            </button>
          </div>
        )}

        {visible.length > 0 && (
          <ul className="orgf-rows">
            {visible.map((event) => {
              const tile = tileParts(event.start_datetime, event.timezone);
              const tone = toneFor(event.status);
              const meta = `${
                event.start_datetime
                  ? formatDateTime(event.start_datetime, event.timezone)
                  : 'No date set'
              }${event.location ? ` · ${event.location}` : ''}`;
              return (
                <li
                  key={event.id}
                  className={`orgf-row orge-row is-${tone}`}
                  onClick={() => navigate(`/organizer/events/${event.id}/edit`)}
                  onKeyDown={(keyEvent) => {
                    if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                      keyEvent.preventDefault();
                      navigate(`/organizer/events/${event.id}/edit`);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  {/*
                    Decorative: the same date is spelled out in full on the meta
                    line below, so a screen reader hearing "06 Dec" twice would
                    be repetition, not information.
                  */}
                  <span className="orge-tile" aria-hidden="true">
                    {tile ? (
                      <>
                        <b>{tile.day}</b>
                        <span>{tile.month}</span>
                      </>
                    ) : (
                      <span className="orge-tiledash">—</span>
                    )}
                  </span>
                  <div className="orgf-rowmain">
                    {/*
                      Title and meta are clipped to one line each; the native
                      tooltip carries the full text so nothing is lost on hover.
                    */}
                    <p className="orgf-rowtitle" title={event.title || undefined}>
                      {event.title}
                    </p>
                    <p className="orgf-rowmeta" title={meta}>
                      {meta}
                    </p>
                  </div>
                  {/*
                    A locked event still opens — the form renders read-only, which
                    is more use than a row that refuses to respond and says nothing
                    about why. The note is here so the click is not a surprise.
                  */}
                  {isLockedStatus(event.status) && (
                    <span className="orgf-rowlock">View only</span>
                  )}
                  <EventCopyLinkButton eventId={event.id} status={event.status} />
                  <EventStatusPill status={event.status} />
                  <span className="orge-chev" aria-hidden="true">›</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}
